import Module from '@/store/resource/common/module';
import Teams from '@/store/resource/teams';
import api from '@/factories/api';
import router from '@/router';
import { compareStrings } from '@/filters/helpers';
import { FEATURES } from '@/access/features';
import {
  findTeamBySlug,
  getRouteTeamSlug,
  setRouterTeam,
  teamSlug,
  teamUrl,
} from '@/lib/teamRoute';
import cloneDeep from 'lodash/cloneDeep';

let module = new Module(new Teams());

module.add('state', {
  // null until resolved for this tab; '' means "no team" (admin, all teams)
  activeTeamId: null,
});

module.add('mutations', {
  setActiveTeamId(state, teamId) {
    state.activeTeamId = teamId ?? null;
  },
});

module.add('getters', {
  filteredTeams(state, getters) {
    if (!getters.search) return state.all;

    return state.all.filter(({ label }) => {
      return compareStrings(label, getters.search);
    });
  },

  activeTeamId(state) {
    return state.activeTeamId;
  },

  currentTeam(state, getters, rootState, rootGetters) {
    return getters.byId(rootGetters['users/userTeam']);
  },

  bySlug(state) {
    return (slug) => findTeamBySlug(state.all, slug);
  },

  getUserTeams(state) {
    return (userId) => {
      return state.all.filter((t) => {
        return (t.users || []).some((id) => id == userId);
      });
    };
  },
});

module.add('actions', {
  /**
   * Decide which team this tab works on, before any team-scoped data loads.
   * Order: legacy `?team_id=`, the `/t/<slug>` URL segment, then the user's
   * last picked team (`selected_team`), then a non-admin's first team.
   * The URL is rewritten to match, so refresh and copy/paste keep the team.
   */
  async resolveActiveTeam({ state, commit, dispatch, getters, rootGetters }) {
    const user = rootGetters['users/currentUser'];
    if (!user) return null;
    if (!rootGetters['users/isFeatureEnabled'](FEATURES.TEAMS)) return null;

    try {
      if (!state.loaded) await dispatch('load');
    } catch (err) {
      console.log('failed to load teams', err);
      return null;
    }

    const queryTeamId = router.currentRoute?.query?.team_id;
    const fromQuery = queryTeamId ? getters.byId(queryTeamId) : null;
    const fromUrl = getters.bySlug(getRouteTeamSlug());
    const fromUser = getters.byId(user.selected_team);
    const fromDefault = rootGetters['users/isAdmin'] ? null : state.all[0];

    const team = fromQuery || fromUrl || fromUser || fromDefault || null;
    const teamId = team?.id || '';

    commit('setActiveTeamId', teamId);
    setRouterTeam(router, teamSlug(team));

    if (queryTeamId) {
      const { team_id, ...query } = router.currentRoute.query;
      router.replace({ query }).catch(() => {});
    }

    // Keep the fallback for team-less URLs valid (e.g. a deleted team).
    const resolvedFromUrl = fromQuery || fromUrl;
    if (!resolvedFromUrl && teamId !== (user.selected_team || '')) {
      dispatch('users/updateUserTeam', teamId || null, { root: true });
    }

    return team;
  },

  /**
   * Switch this tab to another team. The pick is also remembered on the user
   * as the fallback for URLs that don't name a team.
   */
  async switchTeam({ commit, dispatch, getters }, { teamId, reload = true }) {
    const team = getters.byId(teamId) || null;
    await dispatch('users/updateUserTeam', team?.id ?? null, { root: true });

    if (reload) {
      return dispatch('safeReload', team);
    }

    commit('setActiveTeamId', team?.id || '');
    setRouterTeam(router, teamSlug(team));
  },

  updateUserTeams({ dispatch, getters }, updates) {
    const { userId, teams = [] } = updates;
    const userTeams = getters.getUserTeams(userId);

    const removeFromTeams = userTeams.filter((team) => {
      return !teams.includes(team.id);
    });

    removeFromTeams.forEach((team) => {
      dispatch('updateTeamUsers', {
        team_id: team.id,
        users: {
          remove: [userId],
          full: (team.users || []).filter((i) => i != userId),
        },
      });
    });

    const addToTeams = teams
      .filter((teamId) => {
        return !userTeams.some(({ id }) => teamId === id);
      })
      .map((teamId) => getters.byId(teamId));

    addToTeams.forEach((team) => {
      dispatch('updateTeamUsers', {
        team_id: team.id,
        users: {
          add: [userId],
          full: [...(team.users || []), userId],
        },
      });
    });
  },

  addNewUserToTeam({ commit, getters }, data) {
    const { userId, teams } = data;
    if (!teams.length || !userId) return;

    teams.forEach((teamId) => {
      const team = getters.byId(teamId);
      if (team) {
        const updatedTeam = {
          ...cloneDeep(team),
          users: [...(team?.users || []), userId],
        };

        commit('update', { model: {}, update: updatedTeam });
      }
    });
  },

  async updateTeamUsers({ commit }, data) {
    commit('loading', true);
    try {
      const updatedTeam = await api().post('teams/users', data);
      updatedTeam.users = data.users.full;
      commit('update', { model: {}, update: updatedTeam });
    } catch (err) {
      throw err;
    } finally {
      commit('loading', false);
    }
  },

  async getTeamReport({ commit }, team) {
    if (team.report) return;
    try {
      const report = await api().get(`teams/${team.id}/related`);
      commit('update', { model: team, update: { report } });
    } catch (err) {
      throw err;
    }
  },

  async updateResourceTeams({}, { data, resourceId, path }) {
    try {
      const res = await api().put(`${path}/${resourceId}`, data);
    } catch (err) {
      throw err;
    }
  },

  /**
   * Full reload onto `team`, landing on the current section (records from
   * the old team may not exist on the new one).
   */
  safeReload({ getters }, team = getters.currentTeam) {
    const { path, fullPath } = router.currentRoute;

    if (path.includes('users/create')) {
      window.location.href = teamUrl(teamSlug(team), fullPath);
      return;
    }

    const segments = path.split('/').filter((i) => i);
    const depth = segments[0] === 'settings' ? 2 : 1;
    const section = '/' + segments.slice(0, depth).join('/');

    window.location.href = teamUrl(teamSlug(team), section);
  },
});

export default module.create();
